import React, { useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, Col, Row, Card, Spinner } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { format, parse, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { GrowthReportFilterSchemaV4, GrowthReportFilterTypeV4 } from "../../../../services/cm-growth-report/schema/GrowthReportFilterSchemaV4";
import PropertySelectComponent from "./PropertySelectComponent";
import BasedOnComponent from "./BasedOnComponent";

interface HeaderFilterPropsV4 {
    setFilters: (filters: GrowthReportFilterTypeV4) => void;
    isLoading?: boolean;
    hasData?: boolean;
}

const HeaderFilterV4: React.FC<HeaderFilterPropsV4> = ({ setFilters, isLoading, hasData }) => {
    const queryClient = useQueryClient();
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm<GrowthReportFilterTypeV4>({
        resolver: zodResolver(GrowthReportFilterSchemaV4),
        defaultValues: {
            property_id: "",
            base_on: "stay_date",
            compare: [
                {
                    start: format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
                    end: format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
                },
                {
                    start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
                    end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
                },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "compare",
    });

    // Track the last submitted form data to detect changes
    const [appliedFilters, setAppliedFilters] = useState<GrowthReportFilterTypeV4 | null>(null);

    // Compare current form values with last submitted values to detect changes
    const currentFormValues = watch();
    const isFormChanged = appliedFilters
        ? currentFormValues.property_id !== appliedFilters.property_id ||
          currentFormValues.base_on !== appliedFilters.base_on ||
          JSON.stringify(currentFormValues.compare) !== JSON.stringify(appliedFilters.compare)
        : false;

    // Determine which button to show
    const showRefreshButton = hasData && appliedFilters && !isFormChanged;

    const onSubmit = (data: GrowthReportFilterTypeV4) => {
        // If the refresh button is visible and clicked, force React Query to refetch
        if (showRefreshButton) {
            queryClient.invalidateQueries({ queryKey: [
                'report',
                'growth-report',
                'v4'
            ] });
        }

        // Store submitted data for form change comparison
        setAppliedFilters(data);

        setFilters(data);
    };

    const onError = (errorData: any) => {
        console.error("❌ V4 Validation Errors:", JSON.stringify(errorData, null, 2));
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                <form onSubmit={handleSubmit(onSubmit, onError)}>

                    {/* --- BAGIAN 1: FILTER UTAMA (DIRAPIKAN) --- */}
                    {/* Menggunakan Form.Group untuk pengelompokan semantik dan visual yang lebih baik. */}
                    {/* Tata letak diatur agar seimbang di berbagai ukuran layar. */}
                    <Row className="g-4 mb-4 pb-4 border-bottom">
                        <Col xs={12} md={6} lg={5}>
                            <Form.Group>
                                <PropertySelectComponent setValue={setValue} control={control} errors={errors} />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} lg={7}>
                            <Form.Group>
                                <BasedOnComponent control={control} errors={errors} setValue={setValue} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* --- BAGIAN 2: PERIODE PERBANDINGAN DINAMIS --- */}
                    <h5 className="mb-3">Comparison Periods</h5>
                    {fields.map((field, index) => (
                        <Row key={field.id} className="g-2 mb-3 align-items-center">
                            <Col>
                                <Controller
                                    name={`compare.${index}`}
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Form.Group>
                                            <Form.Label className="d-sm-none fw-normal">Period {index + 1}</Form.Label>
                                            <Form.Control
                                                type="month"
                                                value={value.start ? format(new Date(value.start), 'yyyy-MM') : ''}
                                                onChange={(e) => {
                                                    const selectedMonth = e.target.value;
                                                    if (selectedMonth) {
                                                        const date = parse(selectedMonth, 'yyyy-MM', new Date());
                                                        const newStartDate = format(startOfMonth(date), 'yyyy-MM-dd');
                                                        const newEndDate = format(endOfMonth(date), 'yyyy-MM-dd');
                                                        onChange({ start: newStartDate, end: newEndDate });
                                                    }
                                                }}
                                                isInvalid={!!errors.compare?.[index]}
                                            />
                                        </Form.Group>
                                    )}
                                />
                            </Col>
                            <Col xs="auto">
                                <Button
                                    variant="outline-danger"
                                    onClick={() => remove(index)}
                                    disabled={fields.length <= 2}
                                    className="w-100"
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    ))}

                    {errors.compare?.message && (
                        <div className="text-danger mt-2"><small>{errors.compare.message}</small></div>
                    )}

                    {/* --- BAGIAN 3: TOMBOL AKSI --- */}
                    <div className="d-flex flex-column flex-sm-row justify-content-sm-end gap-2 mt-4">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                const newMonth = startOfMonth(new Date());
                                append({
                                    start: format(newMonth, "yyyy-MM-dd"),
                                    end: format(endOfMonth(newMonth), "yyyy-MM-dd"),
                                });
                            }}
                        >
                            + Add Period
                        </Button>
                        {showRefreshButton ? (
                            <Button variant="info" type="submit" disabled={isLoading} className="flex-fill flex-sm-grow-0">
                                {isLoading ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Refreshing...</>
                                ) : (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Report</>
                                )}
                            </Button>
                        ) : (
                            <Button variant="warning" type="submit" disabled={isLoading} className="flex-fill flex-sm-grow-0">
                                {isLoading ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : (
                                    'Show Report'
                                )}
                            </Button>
                        )}
                    </div>
                </form>
            </Card.Body>
        </Card>
    );
};

export default HeaderFilterV4;